'use client';

import { useEffect, useState, useMemo } from 'react';
import { Progress } from '@/shared/ui/shadcn/progress';
import { LoadingAnimation } from '@/shared/ui';
import { RenderStatus } from '@/shared/constants/status';
import { calculateTimeBasedProgress } from '@/shared/lib/render-status';
import { useLoadingContent } from '@/features/design/render/hooks/use-loading-content';
import { useLoadingFacts } from '@/features/design/render/hooks/use-loading-facts';
import type { Job } from '@/features/design/job-queue';

interface AlbumProgressCardProps {
  album: Job;
  projectId?: string;
}

/**
 * Large progress card for album generation.
 * Shows rotating facts/tips with progress indicator on dark background.
 */
export function AlbumProgressCard({
  album,
  projectId,
}: AlbumProgressCardProps) {
  const isQueued = album.status === RenderStatus.PENDING;
  const isProcessing = album.status === RenderStatus.PROCESSING;

  const { data: loadingFactsData } = useLoadingFacts(projectId);

  const { currentContent } = useLoadingContent({
    isActive: isProcessing,
    contextualFacts: loadingFactsData?.facts,
    seenIds: loadingFactsData?.seen_ids,
    projectId,
  });

  // Progress tick for smooth updates
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isProcessing || !album.startedAt) return;
    const interval = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(interval);
  }, [isProcessing, album.startedAt]);

  const progress = useMemo(() => {
    if (!isProcessing || !album.startedAt) return 0;
    return calculateTimeBasedProgress(
      album.startedAt,
      album.durationPredicted,
      album.id
    );
  }, [isProcessing, album.startedAt, album.durationPredicted, album.id, tick]);

  const isStalling = isProcessing && progress >= 95;

  return (
    <div className="flex justify-center w-full">
      <div className="w-full min-h-[200px] md:aspect-video md:max-w-2xl bg-slate-900 rounded-xl flex flex-col items-center justify-center p-6 relative overflow-hidden [container-type:inline-size]">
        {isQueued ? (
          <div className="flex flex-col items-center gap-2">
            <LoadingAnimation type="dots" size="sm" />
            <span className="text-list-item-value text-white/70">Waiting</span>
          </div>
        ) : (
          <>
            {/* Rotating content */}
            {currentContent && (
              <div className="flex-1 flex items-center justify-center w-full max-w-md animate-fade-in">
                <div className="text-center px-4">
                  <p className="text-progress-fact text-white text-balance">
                    {currentContent.text}
                  </p>
                </div>
              </div>
            )}

            {/* Progress section */}
            <div className="w-full mt-auto pt-4 space-y-3">
              <div className="text-center">
                <span className="text-screen-subtitle text-white">
                  {Math.round(progress)}%
                </span>
              </div>
              <Progress
                value={progress}
                className="h-1 bg-white/20"
                isStalling={isStalling}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
